import React from "react";

/**
 * Renders an embedded media block from a URL.
 * Supports: YouTube (any URL), TikTok, Instagram, and direct video files (.mp4/.webm).
 * Falls back to a plain link for anything else.
 */
export default function MediaEmbed({ url, className = "" }) {
  if (!url) return null;
  const u = String(url).trim();

  // YouTube
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) {
    const id = yt[1];
    return (
      <div className={`relative aspect-video w-full max-w-2xl mt-4 ${className}`} data-testid="media-embed-youtube">
        <iframe
          className="absolute inset-0 w-full h-full border-2 border-black rounded-md shadow-brutal"
          src={`https://www.youtube.com/embed/${id}`}
          title="Vidéo YouTube"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  // TikTok — official blockquote embed
  const tt = u.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (tt) {
    return (
      <div className={`mt-4 max-w-md ${className}`} data-testid="media-embed-tiktok">
        <iframe
          className="w-full h-[600px] border-2 border-black rounded-md shadow-brutal"
          src={`https://www.tiktok.com/embed/v2/${tt[1]}`}
          title="Vidéo TikTok"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  // Instagram (reel or post)
  const ig = u.match(/instagram\.com\/(?:reel|p|tv)\/([\w-]+)/);
  if (ig) {
    return (
      <div className={`mt-4 max-w-md ${className}`} data-testid="media-embed-instagram">
        <iframe
          className="w-full h-[600px] border-2 border-black rounded-md shadow-brutal"
          src={`https://www.instagram.com/p/${ig[1]}/embed/`}
          title="Publication Instagram"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }

  // Direct video file
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(u)) {
    return (
      <video
        controls
        className={`mt-4 w-full max-w-2xl border-2 border-black rounded-md shadow-brutal ${className}`}
        data-testid="media-embed-video"
        preload="metadata"
      >
        <source src={u} />
      </video>
    );
  }

  // Fallback — open in new tab
  return (
    <a
      href={u}
      target="_blank"
      rel="noreferrer"
      className={`inline-block mt-3 text-sm font-bold underline text-cef-blue ${className}`}
      data-testid="media-embed-link"
    >
      Voir la ressource ↗
    </a>
  );
}
